/**
 * ROUND8 - dog teams on the map (docs/milestones/ROUND8-contracts.md §2). A team is a rifleman handler (an
 * elite under Elite Garrison) on a patrol beat and a tracker dog at his side (`SpawnDef.handler` = his index
 * in `spawns`). Their job is
 * the trail the ranger leaves, so they work the APPROACH bands he has to cross on the way in - never the
 * compound, the belt or the drop band - each team in a band of its own, on a beat that runs east-west
 * right across it: whichever way he comes north, he crosses the beat, and within a minute or two the dog
 * crosses his trail.
 *
 * How many is the roguelike layer's decision (`params.dogTeams`, src/meta/dogs.ts). What they cost: a
 * team is bought out of the approach bands' share of the threat budget at `COST.dogTeam`, as a patrol of
 * two would be, and takes two places under the garrison cap - the threat's own dogs are part of the
 * garrison that threat buys. The Dog Patrols card's team is the exception: the card "adds" one, so that
 * team comes on top of the budget and the cap makes room for it (the cap's ceiling still holds).
 */

import { atan2, cos, HALF_PI, sin } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import { DOGS } from '../sim/tuningDogs';
import type { EnemyKind, SpawnDef } from '../sim/types';
import { NO_STAND } from './builder';
import { FLAG } from './grid';
import { subdivide } from './patrols';
import { COST, DOG_GEN, LIMITS, MAP } from './tuning';
import type { GenCtx, Point } from './types';

export interface DogTeam {
  handler: SpawnDef;
  dog: SpawnDef;
  band: number;
}

export interface DogTeamsPlaced {
  teams: DogTeam[];
  /** Points the teams took out of the approach bands' garrison budget (`spendBudget`'s `prepaid`). */
  prepaid: number;
}

/** How many teams this map carries: what was asked, held to `DOG_GEN.maxTeams` and to one per approach band. */
export function dogTeamsWanted(ctx: GenCtx): number {
  const asked = Math.max(0, Math.floor(ctx.params.dogTeams ?? 0));
  const bands = ctx.bands.filter((b) => b.kind === 'approach').length;
  return Math.min(asked, DOG_GEN.maxTeams, bands);
}

/** The beat across approach band `band`, rough: west edge to east edge (or the reverse), tilted a little. */
function roughBeat(ctx: GenCtx, rng: Pcg32, band: number): Point[] | null {
  const south = ctx.plan.lines[band - 1];
  const north = ctx.plan.lines[band];
  if (!south || !north) return null;
  const depth = south.z - north.z;
  const mid = (south.z + north.z) / 2 + rng.range(-DOG_GEN.beatJitter, DOG_GEN.beatJitter) * depth;
  const tilt = rng.range(-DOG_GEN.beatJitter, DOG_GEN.beatJitter) * depth;
  const inset = MAP.edge + DOG_GEN.beatInset;
  const ends: Point[] = [{ x: inset, z: mid - tilt }, { x: ctx.sizeX - inset, z: mid + tilt }];
  return rng.chance(0.5) ? ends : ends.reverse();
}

/** Resolve a beat to free cells, handler first; null if any node has nowhere to stand. */
function resolve(ctx: GenCtx, rough: Point[]): Array<[number, number]> | null {
  const nodes: Array<[number, number]> = [];
  for (const p of subdivide(rough)) {
    const spot = ctx.map.nearestOpen(p.x, p.z, 4, NO_STAND);
    if (!spot) return null;
    nodes.push(spot);
  }
  return nodes;
}

/** Put the handler on his beat and the dog at heel, off his shoulder: one team in one band, or null. */
function placeTeam(ctx: GenCtx, rng: Pcg32, band: number, kind: EnemyKind): DogTeam | null {
  for (let tries = 0; tries < DOG_GEN.tries; tries++) {
    const rough = roughBeat(ctx, rng, band);
    const nodes = rough ? resolve(ctx, rough) : null;
    const first = nodes?.[0];
    const second = nodes?.[1];
    if (!nodes || !first || !second) continue;
    const facing = atan2(second[0] - first[0], second[1] - first[1]);
    // At heel: a pace to his left, where the dog walks (`DOGS.heelDistance`).
    const side = facing + HALF_PI;
    const [hx, hz] = [first[0] + sin(side) * DOGS.heelDistance, first[1] + cos(side) * DOGS.heelDistance];
    const heel = ctx.map.nearestOpen(hx, hz, 2, NO_STAND);
    if (!heel) continue;
    const handler = ctx.map.spawn({ kind, x: first[0], z: first[1], facing, patrol: nodes });
    const index = ctx.map.spawns.indexOf(handler);
    const dog = ctx.map.spawn({ kind: 'dog', x: heel[0], z: heel[1], facing, handler: index });
    for (const [x, z] of nodes) ctx.map.mask.circle(x, z, 1.5, FLAG.keep);
    ctx.map.mask.line(nodes, 1.5, FLAG.path);
    return { handler, dog, band };
  }
  return null;
}

/**
 * Place the teams, each in an approach band of its own, before the budget is spent - so the rest of the
 * garrison, the mines and the wire go down around their beats. A team that finds no beat is a problem:
 * the attempt is re-rolled rather than flown with fewer dogs than the briefing promised.
 */
export function placeDogTeams(ctx: GenCtx): DogTeamsPlaced {
  const wanted = dogTeamsWanted(ctx);
  if (wanted === 0) return { teams: [], prepaid: 0 };
  const rng = ctx.rng.fork('dog-teams');
  const bands = ctx.bands.map((b, i) => (b.kind === 'approach' ? i : -1)).filter((i) => i >= 0);
  for (let i = bands.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [bands[i], bands[j]] = [bands[j] ?? 0, bands[i] ?? 0];
  }
  // A rifleman - an elite where the Elite Garrison card says every soldier is one, and paid for as one.
  const elite = ctx.params.modifiers.includes('elite-garrison');
  const teams: DogTeam[] = [];
  for (const band of bands) {
    if (teams.length >= wanted) break;
    const team = placeTeam(ctx, rng, band, elite ? 'elite' : 'rifleman');
    if (team) teams.push(team);
  }
  if (teams.length < wanted) ctx.map.problems.push(`dog teams: only ${teams.length} of ${wanted} found a beat`);
  // The Dog Patrols card adds its team on top: room under the cap, and no charge to the budget.
  const carded = ctx.params.modifiers.includes('dog-patrols') && teams.length > 0 ? 1 : 0;
  ctx.spawnCap = Math.min(LIMITS.spawnsAt9, ctx.spawnCap + 2 * carded);
  return { teams, prepaid: (teams.length - carded) * (COST.dogTeam + (elite ? COST.elite : 0)) };
}

/**
 * Point each dog at its handler again once the garrison is final: the Inside Man's absent guard is taken
 * out of `spawns` after the teams went in, and every index past him moves down one.
 */
export function linkDogTeams(ctx: GenCtx, teams: readonly DogTeam[]): void {
  for (const team of teams) {
    const index = ctx.map.spawns.indexOf(team.handler);
    if (index < 0) ctx.map.problems.push('dog teams: a handler was taken off the map');
    else team.dog.handler = index;
  }
}
